import { FC, memo, useCallback } from "react";
import { styled } from "@mui/material";
import logo from "../../assets/landing/logo.png";
import { contactEmail, footerLinks, mailTo, socialLinks } from "../../utils/landing-data";
import { bg, bp, font, w } from "../../styles/landing-tokens";
import { LinkedinIcon, XIcon } from "./icons";

const Section = styled("footer")({
  position: "relative",
  background: bg,
  /** Figma: books end at y=11367, the logo sits at y=11585, page ends at 12003. */
  paddingTop: 215,
  paddingBottom: 114,
  overflow: "hidden",
  textAlign: "center",

  [bp.md]: { paddingTop: 80, paddingBottom: 80 },
});

/** Figma: 531×218 white 8% pill, blurred 175px, sitting behind the footer. */
const Glow = styled("div")({
  position: "absolute",
  left: "50%",
  bottom: -80,
  transform: "translateX(-50%)",
  width: 531,
  height: 218,
  borderRadius: 200,
  background: w(0.08),
  filter: "blur(175px)",
  pointerEvents: "none",
});

const Inner = styled("div")({
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const Logo = styled("img")({
  width: 201,
  height: 53,
  objectFit: "contain",
});

const Nav = styled("nav")({
  display: "flex",
  alignItems: "center",
  gap: 33,
  marginTop: 67,

  [bp.sm]: { flexWrap: "wrap", justifyContent: "center", gap: 20, marginTop: 40 },
});

const navLinkStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: font.serifAlt,
  fontWeight: 500,
  fontSize: 22,
  lineHeight: "26.4px",
  letterSpacing: "0.03em",
  color: "#FFFFFF",
  opacity: 0.9,
  transition: "opacity .2s ease",

  "&:hover": { opacity: 1 },

  [bp.sm]: { fontSize: 17 },
} as const;

const NavLink = styled("button")(navLinkStyle);

/** "Career" leaves the page, so it needs to be a real anchor — same as the header. */
const NavAnchor = styled("a")({ ...navLinkStyle, textDecoration: "none" });

const Socials = styled("div")({
  display: "flex",
  gap: 27,
  /** Figma: nav ends at y=11731, socials start at y=11768. */
  marginTop: 37,
});

const Social = styled("a")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 53,
  height: 53,
  borderRadius: "50%",
  background: "#000000",
  border: "0.93px solid #575757",
  transition: "border-color .2s ease",

  "&:hover": { borderColor: "#FFFFFF" },
});

const Email = styled("a")({
  marginTop: 36,
  fontFamily: font.serifAlt,
  fontWeight: 700,
  fontSize: 26.6,
  lineHeight: "31.9px",
  letterSpacing: "0.03em",
  color: "#FFFFFF",
  textDecoration: "none",

  "&:hover": { textDecoration: "underline" },

  [bp.sm]: { fontSize: 20 },
});

export const LandingFooter: FC = memo(() => {
  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <Section id="contact">
      <Glow />

      <Inner>
        <Logo src={logo} alt="Evan Szu" />

        <Nav>
          {footerLinks.map((l) =>
            l.href ? (
              <NavAnchor key={l.name} href={l.href} target="_blank" rel="noreferrer">
                {l.name}
              </NavAnchor>
            ) : (
              <NavLink key={l.name} onClick={() => scrollTo(l.id)}>
                {l.name}
              </NavLink>
            )
          )}
        </Nav>

        <Socials>
          <Social
            href={socialLinks.linkedin}
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
          >
            <LinkedinIcon size={32} />
          </Social>
          <Social href={socialLinks.x} target="_blank" rel="noreferrer" aria-label="X">
            <XIcon size={26} />
          </Social>
        </Socials>

        <Email href={mailTo("Reaching out via your website")}>{contactEmail}</Email>
      </Inner>
    </Section>
  );
});
